import { AddressModel } from "@app/data/engine.ts";
import useLookupListData from "@app/data-hooks/use-lookup-list-data.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import useAuth from "@app/hooks/use-auth.ts";
import { Button, Grid2, Stack, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { QueryStatusRouter, Select } from "bi-lib";
import { useMemo } from "react";

export default function AccountContactForm() {
    const { userData } = useAuth();

    const staffId = useMemo(() => userData?.UserId, [userData]);

    const { staff, saveContact, companyList } = useStaffData({ id: staffId });
    const { stateCodeList } = useLookupListData();

    const form = useForm({
        defaultValues: {
            firstName: staff.data?.firstName,
            middleInitial: staff.data?.middleInitial,
            lastName: staff.data?.lastName,
            initials: staff.data?.initials,
            email: staff.data?.email,
            staffCompanyId: staff.data?.staffCompanyId,
            title: staff.data?.title,
            address1: staff.data?.address?.address1,
            address2: staff.data?.address?.address2,
            city: staff.data?.address?.city,
            state: staff.data?.address?.stateCodeId,
            postalCode: staff.data?.address?.postalCode,
            homePhone: staff.data?.homePhone,
            homeFax: staff.data?.homeFax,
            workPhone: staff.data?.workPhone,
            workFax: staff.data?.workFax,
        },
        onSubmit: ({ value }) => {
            saveContact.mutate({
                staffingId: staffId,
                firstName: value.firstName,
                middleInitial: value.middleInitial,
                lastName: value.lastName,
                initials: value.initials,
                email: value.email,
                staffCompanyId: value.staffCompanyId,
                title: value.title,
                address: {
                    address1: value.address1,
                    address2: value.address2,
                    city: value.city,
                    state: value.state,
                    postalCode: value.postalCode,
                } as AddressModel,
                homePhone: value.homePhone,
                homeFax: value.homeFax,
                workPhone: value.workPhone,
                workFax: value.workFax,
                roleId: staff.data?.roleId,
            });
        },
    });

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
        >
            <QueryStatusRouter queryHook={staff}>
                <Grid2 container spacing={1}>
                    <Grid2 size={4}>
                        <form.Field name={"firstName"}>
                            {(field) => (
                                <TextField
                                    label={"First Name"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                    required
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={2}>
                        <form.Field name={"middleInitial"}>
                            {(field) => (
                                <TextField
                                    label={"Middle Initial"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"lastName"}>
                            {(field) => (
                                <TextField
                                    label={"Last Name"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                    required
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={2}>
                        <form.Field name={"initials"}>
                            {(field) => (
                                <TextField
                                    label={"Initials"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                    required
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"email"}>
                            {(field) => (
                                <TextField
                                    label={"Email"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                    type={"email"}
                                    required
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={4}>
                        <QueryStatusRouter queryHook={companyList}>
                            <form.Field name={"staffCompanyId"}>
                                {(field) => (
                                    <Select
                                        label={"Company"}
                                        value={field.state.value}
                                        onChange={(e) => field.handleChange(e)}
                                        options={companyList.data?.map((x) => ({
                                            label: x.companyName,
                                            value: x.staffCompanyId,
                                        }))}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </QueryStatusRouter>
                    </Grid2>
                    <Grid2 size={4}>
                        <form.Field name={"title"}>
                            {(field) => (
                                <TextField
                                    label={"Title"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value ?? "")}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <Stack spacing={1}>
                            <form.Field name={"address1"}>
                                {(field) => (
                                    <TextField
                                        label={"Address 1"}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value ?? "")}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"address2"}>
                                {(field) => (
                                    <TextField
                                        label={"Address 2"}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value ?? "")}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"city"}>
                                {(field) => (
                                    <TextField
                                        label={"City"}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value ?? "")}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Stack>
                    </Grid2>
                    <Grid2 size={6}>
                        <Stack spacing={1}>
                            <QueryStatusRouter queryHook={stateCodeList}>
                                <form.Field name={"state"}>
                                    {(field) => (
                                        <Select
                                            label={"State"}
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e)}
                                            fullWidth
                                            options={stateCodeList.data?.map((x) => ({
                                                label: x.stateName,
                                                value: x.stateCodeId,
                                            }))}
                                        />
                                    )}
                                </form.Field>
                            </QueryStatusRouter>
                            <form.Field name={"postalCode"}>
                                {(field) => (
                                    <TextField
                                        label={"Postal Code"}
                                        type={"number"}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Stack>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"homePhone"}>
                            {(field) => (
                                <TextField
                                    label={"Home Phone"}
                                    type={"tel"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"homeFax"}>
                            {(field) => (
                                <TextField
                                    label={"Home Fax"}
                                    type={"tel"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"workPhone"}>
                            {(field) => (
                                <TextField
                                    label={"Work Phone"}
                                    type={"tel"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                    <Grid2 size={6}>
                        <form.Field name={"workFax"}>
                            {(field) => (
                                <TextField
                                    label={"Work Fax"}
                                    type={"tel"}
                                    value={field.state.value ?? ""}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                    fullWidth
                                />
                            )}
                        </form.Field>
                    </Grid2>
                </Grid2>
            </QueryStatusRouter>
            <Button variant={"contained"} type={"submit"} sx={{ mt: 1 }} fullWidth>
                Save
            </Button>
        </form>
    );
}
